import { useEffect, useState } from "react";
import {
  Warning,
  Door,
  Clock,
  CalendarX,
  Confetti,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useGetAvailableRoomsForSlotQuery } from "../../../../store/services/roomsApi";
import { suggestNearestFreeSlots } from "../../../../utils/roomSuggestions";
import { formatTime } from "../../../../utils/dateUtils";
import "./RoomBlockedPanel.css";

/**
 * Shown instead of the bare room error line when the selected room is blocked.
 * Explains exactly why (taken / closed day / outside hours / holiday), offers
 * one-click fixes (free rooms, nearest free times in this room) and — for
 * admins only — a "book anyway" waiver.
 */
function RoomBlockedPanel({ form }) {
  const {
    clinicId,
    therapistId,
    formData,
    updateField,
    roomConflicts,
    roomAvailabilityError,
    roomAvailabilityReason,
    isAdmin,
    roomOverrideConfirmed,
    setRoomOverrideConfirmed,
    appointment,
  } = form;

  const durationMins = parseInt(formData.duration, 10) || 45;
  const startISO =
    formData.date && formData.startTime
      ? new Date(`${formData.date}T${formData.startTime}`).toISOString()
      : null;
  const endISO = startISO
    ? new Date(new Date(startISO).getTime() + durationMins * 60000).toISOString()
    : null;

  // Alternative rooms free at this slot
  const { data: roomsForSlot = [] } = useGetAvailableRoomsForSlotQuery(
    {
      clinicId,
      startTime: startISO,
      endTime: endISO,
      therapistId,
      excludeAppointmentId: appointment?.id || null,
    },
    { skip: !clinicId || !startISO }
  );
  const freeRooms = roomsForSlot.filter(
    (r) => r.isAvailable && r.room.id !== formData.roomId
  );

  // Nearest free times in the selected room
  const [freeSlots, setFreeSlots] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setFreeSlots([]);
    if (!clinicId || !formData.roomId || !formData.date || !formData.startTime) {
      return undefined;
    }
    suggestNearestFreeSlots({
      clinicId,
      roomId: formData.roomId,
      dateStr: formData.date,
      startTime: formData.startTime,
      durationMins,
      excludeAppointmentId: appointment?.id || null,
    }).then((slots) => {
      if (!cancelled) setFreeSlots(slots);
    });
    return () => {
      cancelled = true;
    };
  }, [clinicId, formData.roomId, formData.date, formData.startTime, durationMins, appointment?.id]);

  const conflict = roomConflicts[0] || null;
  const reason = roomAvailabilityReason?.reason || null;

  // Patient names are shown only to the admin or the conflicting therapist
  const conflictLabel = (() => {
    if (!conflict) return null;
    if (conflict.isBlock) return "זמן חסום ביומן";
    const canSeeName =
      isAdmin || (conflict.therapistId && conflict.therapistId === therapistId);
    if (conflict.patientName && canSeeName) return conflict.patientName;
    return "פגישה של מטפל/ת אחר/ת";
  })();

  return (
    <div className={`room-blocked-panel ${roomOverrideConfirmed ? "overridden" : ""}`}>
      <div className="room-blocked-reason">
        {conflict ? (
          <>
            <Door size={15} weight="fill" />
            <span>
              החדר תפוס בין {formatTime(conflict.startTime)}–
              {formatTime(conflict.endTime)}
              {conflictLabel ? ` · ${conflictLabel}` : ""}
            </span>
          </>
        ) : reason === "holiday" ? (
          <>
            <Confetti size={15} weight="fill" />
            <span>{roomAvailabilityError}</span>
          </>
        ) : reason === "closed_day" ? (
          <>
            <CalendarX size={15} weight="fill" />
            <span>{roomAvailabilityError}</span>
          </>
        ) : (
          <>
            <Clock size={15} weight="fill" />
            <span>{roomAvailabilityError}</span>
          </>
        )}
      </div>

      {freeRooms.length > 0 && (
        <div className="room-blocked-suggestions">
          <span className="room-blocked-suggestions-label">חדרים פנויים:</span>
          <div className="room-blocked-chips">
            {freeRooms.slice(0, 4).map(({ room }) => (
              <button
                key={room.id}
                type="button"
                className="room-blocked-chip"
                onClick={() => updateField("roomId", room.id)}
              >
                <Door size={12} /> {room.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {freeSlots.length > 0 && (
        <div className="room-blocked-suggestions">
          <span className="room-blocked-suggestions-label">
            שעות פנויות בחדר זה:
          </span>
          <div className="room-blocked-chips">
            {freeSlots.map((slot) => (
              <button
                key={slot.start}
                type="button"
                className="room-blocked-chip"
                onClick={() => updateField("startTime", slot.start)}
              >
                <Clock size={12} /> {slot.start}
              </button>
            ))}
          </div>
        </div>
      )}

      {isAdmin &&
        (roomOverrideConfirmed ? (
          <div className="room-blocked-override-note">
            <ShieldCheck size={14} weight="fill" />
            <span>הפגישה תיקבע בחדר זה למרות החסימה</span>
          </div>
        ) : (
          <div className="room-blocked-override">
            <span className="room-blocked-override-text">
              <Warning size={13} weight="fill" /> ניתן לקבוע בכל זאת (מנהל בלבד)
            </span>
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={() => setRoomOverrideConfirmed(true)}
            >
              קבע בכל זאת
            </button>
          </div>
        ))}
    </div>
  );
}

export default RoomBlockedPanel;
